// components/BillHistoryTable.tsx
import { useViewBill } from "@shared/api/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { useParams } from "react-router-dom";
import { useOutstandingBills } from "../../hooks";
import { useState } from "react";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";

export function BillHistoryTable() {
  const currency = useCurrency();
  const params = useParams();
  const viewBill = useViewBill();
  const [currentPage, setCurrentPage] = useState(1);
  const { data: billHistoryData, isLoading } = useOutstandingBills({
    remoteUtilityId: getRemoteUtilityId(),
    remoteConsumerNumber: params.consumerNumber || "",
    page: currentPage,
    limit: MODAL_PAGE_SIZE,
  });

  const totalItems = billHistoryData?.count || 0;
  const totalPages = Math.ceil(totalItems / MODAL_PAGE_SIZE) || 1;

  const handleViewClick = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);
      // showToast('Bill downloaded successfully', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      // showToast('Failed to download the bill', 'error');
    }
  };

  const columns: TableColumn<any>[] = [
    {
      key: "billingPeriod",
      header: "Bill Period",
      type: "text",
    },
    {
      key: "billNumber",
      header: "Invoice No",
      type: "text",
    },
    {
      key: "dueDate",
      header: "Due Date",
      type: "text",
    },
    {
      key: "daysPassed",
      header: "Days Overdue",
      type: "custom",
      render: (value: number) => (
        <span
          className={value > 0 ? "text-red-600 font-medium" : "text-gray-600"}
        >
          {value > 0 ? `${value} days` : "—"}
        </span>
      ),
    },
    {
      key: "billStatusLabel",
      header: "Bill Status",
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getStatusBadgeClass(value),
      },
    },
    {
      key: "paymentStatus",
      header: "Payment Status",
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getStatusBadgeClass(value),
      },
    },
    {
      key: "lastPayAmount",
      header: "Last Paid",
      type: "custom",
      render: (value: any, row: any) => {
        if (!row.lastPayAmount || !row.lastPayAmount) {
          return <span className="text-gray-400">—</span>;
        }
        return (
          <div>
            <p className="text-sm font-medium text-gray-900">
              {currency} {row.lastPayAmount}
            </p>
          </div>
        );
      },
    },
    {
      key: "billAmount",
      header: "Amount",
      type: "custom",
      render: (value: number, row: any) => {
        if (row.paymentStatus === "Partial") {
          return (
            <div>
              <p className="text-sm font-medium text-orange-600">
                {currency} {value}
              </p>
              <p className="text-xs text-gray-500">
                of {currency} {row.totalAmount}
              </p>
            </div>
          );
        }
        return (
          <span className="text-sm font-medium text-gray-900">
            {currency} {value}
          </span>
        );
      },
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View Bill",
      onClick: (row: any) => {
        handleViewClick(row.id);
      },
      icon: "Eye",
      variant: "outline",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Bill History</h3>
          <p className="text-sm text-gray-600 mt-1">
            View all bills and their payment status
          </p>
        </div>
      </div>

      <GenericTable
        data={billHistoryData?.result || []}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        emptyMessage="No bills found"
        loading={isLoading}
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: MODAL_PAGE_SIZE,
          startItem: totalItems === 0 ? 0 : (currentPage - 1) * MODAL_PAGE_SIZE + 1,
          endItem: Math.min(currentPage * MODAL_PAGE_SIZE, totalItems),
        }}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
